'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import OtpModal from './OtpModal';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  isSeller?: boolean;
}

export default function BuyerLoginForm({ isSeller = false }: Props) {
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const router = useRouter();
  const { signInWithPhone } = useAuth();

  const validatePhone = (value: string) => {
    if (!value) return 'Mobile number is required';
    if (!/^\d{10}$/.test(value)) return 'Enter a valid 10-digit mobile number';
    return '';
  };

  const handleSendOtp = async () => {
    const error = validatePhone(phone);
    if (error) {
      setPhoneError(error);
      return;
    }
    setPhoneError('');
    setIsLoading(true);
    try {
      await signInWithPhone(phone);
      toast.success(`OTP sent to +91 ${phone}`);
      setOtpOpen(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = async () => {
    setOtpOpen(false);
    toast.success('Welcome to LocalMandi Mysore!');
    await new Promise((r) => setTimeout(r, 400));
    if (isSeller) {
      router.push('/seller-dashboard');
    } else {
      router.push('/marketplace');
    }
  };

  return (
    <>
      <div className="space-y-5">
        {/* Phone Number */}
        <div>
          <label htmlFor="buyer-phone" className="block text-sm font-600 text-foreground mb-1.5">
            Mobile Number
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-lg border border-border bg-muted text-sm text-muted-foreground font-600 select-none">
              +91
            </div>
            <input
              id="buyer-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(val);
                if (phoneError) setPhoneError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendOtp();
              }}
              className={`input-field flex-1 ${phoneError ? 'input-error' : ''}`}
              placeholder="98765 43210"
              inputMode="numeric"
              autoComplete="tel"
            />
          </div>
          {phoneError && (
            <p className="text-xs text-negative mt-1">{phoneError}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1.5">
            We&apos;ll send a 6-digit OTP to your mobile number
          </p>
        </div>

        {/* Send OTP Button */}
        <button
          type="button"
          disabled={isLoading}
          onClick={handleSendOtp}
          className="w-full py-2.5 rounded-lg text-sm font-700 btn-primary flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              <span>Sending OTP...</span>
            </>
          ) : (
            <>
              <Icon name="DevicePhoneMobileIcon" size={16} />
              <span>Send OTP via SMS</span>
            </>
          )}
        </button>
      </div>

      <OtpModal
        open={otpOpen}
        phone={phone}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        isSeller={isSeller}
      />
    </>
  );
}